import { environment } from 'src/environments/environment';
import { InjectionToken, Signal } from '@angular/core';
import type { UnifiedTask } from '@shared/definitions/unified-task.model';
import type { TaskCard, TaskStatus } from '@shared/definitions/task-card.model';
import type { ServiceProvider } from '@shared/definitions/service-provider';

/**
 * Pattern B contract + DI token for the Task Management aggregation feature —
 * lifted to shared/definitions so lower layers can inject it without importing
 * the feature. Concrete impls + provideTaskManagementStore() stay in pages; the
 * provider is wired at the app root (app.config), this token carries NO factory.
 */
export interface ITaskManagementStore {
  readonly tasks: Signal<readonly UnifiedTask[]>;
  readonly loading: Signal<boolean>;
  readonly lastError: Signal<Error | null>;

  readonly totalCount: Signal<number>;
  readonly byStatus: Signal<Readonly<Record<TaskStatus, readonly UnifiedTask[]>>>;

  refresh(): Promise<void>;

  getTasksForProperty(propertyUID: string): readonly UnifiedTask[];
  getTasksByStatus(status: TaskStatus): readonly UnifiedTask[];
  getTaskById(taskId: string): UnifiedTask | null;

  toKanbanCards(tasks?: readonly UnifiedTask[]): TaskCard[];

  updateTaskStatus(taskId: string, newStatus: TaskStatus): Promise<void>;
  assignServiceProvider(taskId: string, provider: ServiceProvider): Promise<void>;
  updateTaskSchedule(taskId: string, scheduledDate: Date): Promise<void>;
  updateTaskCompletionData(
    taskId: string,
    completionData: {
      completionNotes?: string;
      rating?: number;
      costActual?: number;
      durationActual?: number;
      issues?: string[];
      recommendations?: string;
    }
  ): Promise<void>;
}

export const TASK_MANAGEMENT_STORE = new InjectionToken<ITaskManagementStore>('TASK_MANAGEMENT_STORE');

export type TaskManagementStoreMode = 'mock' | 'firestore';

export function resolveTaskManagementStoreMode(): TaskManagementStoreMode {
  if (typeof window === 'undefined') return 'firestore';
  if (environment.production) return 'firestore'; // QA-only mock store: never honour ?store=mock in production (silent data loss).
  const url = new URL(window.location.href);
  const param = url.searchParams.get('store');
  if (param === 'firestore' || param === 'mock') {
    try {
      window.localStorage.setItem('taskManagement.storeMode', param);
    } catch {
      /* ignore */
    }
    return param;
  }
  try {
    const stored = window.localStorage.getItem('taskManagement.storeMode');
    if (stored === 'firestore' || stored === 'mock') return stored;
  } catch {
    /* ignore */
  }
  return 'firestore';
}
